import { TmState, ValidationRule, ValidationRuleEntry, PayloadSchemaProperty } from "./TestCaseTypes";

export const formatTreeHeaderLabel = (label: string): string => {
  const spaced = label
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();

  return spaced
    .split(" ")
    .map((word) => {
      if (word.toUpperCase() === word) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

/**
 * Parses a path like "Data[0].Fss_3v3_current" into tokens.
 * Returns: ['Data', '[0]', 'Fss_3v3_current']
 */
const parsePathTokens = (path: string): string[] => {
  const tokens: string[] = [];
  let current = "";
  for (let i = 0; i < path.length; i++) {
    const char = path[i];
    if (char === ".") {
      if (current) {
        tokens.push(current);
        current = "";
      }
    } else if (char === "[") {
      if (current) {
        tokens.push(current);
        current = "";
      }
      let j = i;
      while (j < path.length && path[j] !== "]") j++;
      tokens.push(path.slice(i, j + 1));
      i = j;
    } else {
      current += char;
    }
  }
  if (current) tokens.push(current);
  return tokens;
};

const toPathParts = (path: string): string[] => parsePathTokens(path);

const getByPath = (obj: unknown, path: string): unknown => {
  let cur = obj;
  for (const part of toPathParts(path)) {
    if (cur == null) return undefined;
    if (part.startsWith("[") && part.endsWith("]")) {
      const idx = Number(part.slice(1, -1));
      if (!Array.isArray(cur)) return undefined;
      cur = cur[idx];
    } else {
      cur = (cur as Record<string, unknown>)[part];
    }
  }
  return cur;
};

const inferArrayIndexesFromPayload = (payloadValue: unknown): number[] => {
  if (!Array.isArray(payloadValue)) return [0];
  if (payloadValue.length === 0) return [0];
  return payloadValue.map((_, idx) => idx);
};

const appendLeaf = (acc: { key: string; displayName: string }[], key: string, displayName: string): void => {
  if (!acc.some((item) => item.key === key)) {
    acc.push({ key, displayName });
  }
};

const flattenSchemaWithPayload = (
  schema: { type?: string; properties?: Record<string, PayloadSchemaProperty> } | undefined,
  payload: Record<string, unknown>,
  prefix = "",
  labelPrefix = ""
): { key: string; displayName: string }[] => {
  if (!schema?.properties) return [];
  const acc: { key: string; displayName: string }[] = [];

  Object.entries(schema.properties).forEach(([k, prop]) => {
    const keyPath = prefix ? `${prefix}.${k}` : k;
    const labelPath = labelPrefix ? `${labelPrefix}.${k}` : k;

    if (prop.type === "object" && prop.properties) {
      acc.push(...flattenSchemaWithPayload(prop, payload, keyPath, labelPath));
      return;
    }

    if (prop.type === "array") {
      const payloadValue = getByPath(payload, keyPath);
      const indexes = inferArrayIndexesFromPayload(payloadValue);

      if (prop.items?.type === "object" && prop.items.properties) {
        indexes.forEach((idx) => {
          const itemPayload = Array.isArray(payloadValue) ? payloadValue[idx] : payloadValue;
          // Retain array index prefix (e.g. Data[0]) so nested object properties preserve hierarchy
          acc.push(...flattenSchemaWithPayload(prop.items, itemPayload as Record<string, unknown>, `${keyPath}[${idx}]`, `${labelPath}[${idx}]`));
        });
      } else {
        indexes.forEach((idx) => appendLeaf(acc, `${keyPath}[${idx}]`, `${labelPath}[${idx}]`));
      }
      return;
    }

    appendLeaf(acc, keyPath, labelPath);
  });

  return acc;
};

export const flattenPayload = (
  obj: Record<string, unknown>,
  prefix = "",
  schema?: { type?: string; properties?: Record<string, PayloadSchemaProperty> }
): { key: string; displayName: string }[] => {
  if (schema?.properties) {
    return flattenSchemaWithPayload(schema, obj, prefix);
  }
  if (!obj || typeof obj !== "object") return [];
  return Object.entries(obj).flatMap(([k, v]) => {
    const fullKey = prefix ? `${prefix}.${k}` : k;
    if (Array.isArray(v)) {
      const indexes = v.length > 0 ? v.map((_, idx) => idx) : [0];
      return indexes.flatMap((i) => {
        const item = v[i];
        if (item !== null && typeof item === "object" && !Array.isArray(item)) {
          return flattenPayload(item as Record<string, unknown>, `${fullKey}[${i}]`);
        }
        return [{ key: `${fullKey}[${i}]`, displayName: `${k}[${i}]` }];
      });
    }
    if (v !== null && typeof v === "object") {
      return flattenPayload(v as Record<string, unknown>, fullKey);
    }
    return [{ key: fullKey, displayName: k }];
  });
};

/**
 * Recursively walks a payloadSchema and returns a flat map of
 * dotted-path key → enumMapping for every field that has one.
 * Matches the same key format produced by flattenPayload.
 */
export const flattenSchemaEnumMappings = (
  schema: { type?: string; properties?: Record<string, PayloadSchemaProperty> } | undefined,
  prefix = ""
): Record<string, Record<string, string>> => {
  const result: Record<string, Record<string, string>> = {};
  if (!schema?.properties) return result;
  Object.entries(schema.properties).forEach(([k, prop]) => {
    const fullKey = prefix ? `${prefix}.${k}` : k;
    if (prop.enumMapping) {
      result[fullKey] = prop.enumMapping;
    }
    if (prop.type === "object" && prop.properties) {
      Object.assign(result, flattenSchemaEnumMappings(prop, fullKey));
    }
    if (prop.type === "array") {
      if (prop.items?.type === "object" && prop.items.properties) {
        Object.assign(result, flattenSchemaEnumMappings(prop.items, `${fullKey}[0]`));
      } else if (prop.items?.enumMapping) {
        result[`${fullKey}[0]`] = prop.items.enumMapping;
      }
    }
  });
  return result;
};

/**
 * Recursively walks a payloadSchema and returns a flat map of
 * dotted-path key → schema type string for every leaf field.
 */
export const flattenSchemaTypes = (
  schema: { type?: string; properties?: Record<string, PayloadSchemaProperty> } | undefined,
  prefix = ""
): Record<string, string> => {
  const result: Record<string, string> = {};
  if (!schema?.properties) return result;
  Object.entries(schema.properties).forEach(([k, prop]) => {
    const fullKey = prefix ? `${prefix}.${k}` : k;
    result[fullKey] = prop.type;
    if (prop.type === "object" && prop.properties) {
      Object.assign(result, flattenSchemaTypes(prop, fullKey));
    }
    if (prop.type === "array") {
      if (prop.items?.type === "object" && prop.items.properties) {
        Object.assign(result, flattenSchemaTypes(prop.items, `${fullKey}[0]`));
      } else {
        result[`${fullKey}[0]`] = prop.items?.type ?? "string";
      }
    }
  });
  return result;
};

export const setNestedValue = (obj: Record<string, unknown>, path: string, value: unknown): void => {
  const parts = parsePathTokens(path);
  if (parts.length === 0) return;

  let cur: unknown = obj;

  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    const nextPart = parts[i + 1];
    const idxMatch = part.match(/^\[(\d+)\]$/);
    const nextIsIndex = /^\[\d+\]$/.test(nextPart ?? "");

    if (idxMatch) {
      // Current token is an array index — cur must be an array
      const idx = Number(idxMatch[1]);
      const arr = cur as unknown[];
      if (arr[idx] == null) {
        arr[idx] = nextIsIndex ? [] : {};
      }
      cur = arr[idx];
      continue;
    }

    // Current token is an object key — next determines what to create if missing
    const asObj = cur as Record<string, unknown>;
    if (asObj[part] == null) {
      asObj[part] = nextIsIndex ? [] : {};
    }
    cur = asObj[part];
  }

  const last = parts[parts.length - 1];
  const idxMatch = last.match(/^\[(\d+)\]$/);
  if (idxMatch) {
    // Last token is an array index
    (cur as unknown[])[Number(idxMatch[1])] = value;
  } else {
    (cur as Record<string, unknown>)[last] = value;
  }
};

export const getNestedValue = (obj: unknown, path: string): unknown => {
  const parts = parsePathTokens(path);
  if (parts.length === 0) return undefined;
  
  let cur = obj;
  for (const part of parts) {
    if (cur == null) return undefined;
    const idxMatch = part.match(/^\[(\d+)\]$/);
    if (idxMatch) {
      if (Array.isArray(cur)) {
        cur = cur[Number(idxMatch[1])];
      } else {
        cur = (cur as Record<string, unknown>)[idxMatch[1]];
      }
    } else {
      cur = (cur as Record<string, unknown>)[part];
    }
  }
  return cur;
};

const normalizeArrayIndexKey = (key: string): string => key.replace(/\[\d+\]/g, "[0]");

const buildValidationObj = (
  rule: ValidationRule,
  paramType?: string,
  enumMapping?: Record<string, string>
): Record<string, unknown> => {
  if (rule.type === "range") {
    let min: unknown = rule.min;
    let max: unknown = rule.max;
    if (paramType === "integer") {
      const parsedMin = parseInt(rule.min, 10);
      const parsedMax = parseInt(rule.max, 10);
      min = isNaN(parsedMin) ? rule.min : parsedMin;
      max = isNaN(parsedMax) ? rule.max : parsedMax;
    } else if (paramType === "number") {
      const parsedMin = parseFloat(rule.min);
      const parsedMax = parseFloat(rule.max);
      min = isNaN(parsedMin) ? rule.min : parsedMin;
      max = isNaN(parsedMax) ? rule.max : parsedMax;
    }
    return { isValidate: true, type: "range", min, max };
  }
  if (rule.type === "enumMatch") {
    const parsedMin = parseInt(rule.min, 10);
    const parsedMax = parseInt(rule.max, 10);
    const min: unknown = isNaN(parsedMin) ? rule.min : parsedMin;
    const max: unknown = isNaN(parsedMax) ? rule.max : parsedMax;
    return { isValidate: true, type: "enumMatch", min, max };
  }
  let value: unknown = rule.value;
  if (enumMapping) {
    // Enum param: send the label string if the key exists, otherwise send raw value as string
    value = enumMapping[rule.value] ?? rule.value;
  } else if (paramType === "integer") {
    const parsed = parseInt(rule.value, 10);
    value = isNaN(parsed) ? rule.value : parsed;
  } else if (paramType === "number") {
    const parsed = parseFloat(rule.value);
    value = isNaN(parsed) ? rule.value : parsed;
  } else if (paramType === "boolean") {
    if (rule.value === "true") value = true;
    else if (rule.value === "false") value = false;
  }
  return { isValidate: true, type: rule.type === "equal" ? "equalto" : "constant", value };
};

export const buildNestedValidation = (
  params: { key: string }[],
  tmState: TmState | undefined,
  enumMappings?: Record<string, Record<string, string>>,
  paramTypes?: Record<string, string>
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  const allKeys = new Set<string>(params.map((p) => p.key));
  Object.keys(tmState?.validations ?? {}).forEach((key) => allKeys.add(key));
  Object.values(tmState?.arrayGroups ?? {}).forEach((group) => {
    group.indices.forEach((idx) => {
      if (group.itemType === "object") {
        group.objectFields.forEach((field) => allKeys.add(`${group.basePath}[${idx}].${field}`));
      } else {
        allKeys.add(`${group.basePath}[${idx}]`);
      }
    });
  });

  Array.from(allKeys).forEach((key) => {
    const isChecked = tmState?.checkedParams.includes(key) ?? false;
    const rule = tmState?.validations[key];
    const normalizedKey = normalizeArrayIndexKey(key);
    const resolvedType = paramTypes?.[key] ?? paramTypes?.[normalizedKey];
    const resolvedEnum = enumMappings?.[key] ?? enumMappings?.[normalizedKey];
    const validationObj = isChecked && rule
      ? buildValidationObj(rule, resolvedType, resolvedEnum)
      : { isValidate: false };
    setNestedValue(result, key, validationObj);
  });
  return result;
};

/**
 * Walks a nested saved-validation object and returns all flat dotted/bracket
 * keys whose values are validation leaf objects (contain `isValidate`).
 * This is used to discover dynamically-added array indices from a previous save.
 */
const flattenValidationKeys = (obj: unknown, prefix = ""): string[] => {
  if (obj == null || typeof obj !== "object") return [];
  // Leaf = real validation-rule object → has BOTH `isValidate` AND `type`.
  // Group nodes (Sys_time_config etc.) also carry a sibling `isValidate`
  // flag but have no `type` at that level — don't stop recursion there.
  if (!Array.isArray(obj) && "isValidate" in (obj as object) && "type" in (obj as object)) {
    return prefix ? [prefix] : [];
  }
  const result: string[] = [];
  if (Array.isArray(obj)) {
    (obj as unknown[]).forEach((item, idx) => {
      result.push(...flattenValidationKeys(item, prefix ? `${prefix}[${idx}]` : `[${idx}]`));
    });
  } else {
    Object.entries(obj as Record<string, unknown>).forEach(([k, v]) => {
      const isNumeric = /^\d+$/.test(k);
      const childPath = isNumeric
        ? (prefix ? `${prefix}[${k}]` : `[${k}]`)
        : (prefix ? `${prefix}.${k}` : k);
      result.push(...flattenValidationKeys(v, childPath));
    });
  }
  return result;
};

/**
 * Scans flat param keys for array-index patterns and builds the `arrayGroups`
 * metadata needed by `buildNestedValidation` to emit all indices on save.
 */
const RECON_ARRAY_RE = /^(.*?)\[(\d+)\](?:\.(.*))?$/;

const buildArrayGroupsFromParamKeys = (
  keys: string[]
): Record<string, { basePath: string; itemType: "primitive" | "object"; objectFields: string[]; indices: number[] }> | undefined => {
  const groups: Record<string, { basePath: string; itemType: "primitive" | "object"; objectFields: string[]; indices: number[] }> = {};
  keys.forEach((key) => {
    const match = key.match(RECON_ARRAY_RE);
    if (!match) return;
    const basePath = match[1];
    const idx = Number(match[2]);
    const fieldPath = match[3] ?? "";
    if (!groups[basePath]) {
      groups[basePath] = { basePath, itemType: fieldPath ? "object" : "primitive", objectFields: [], indices: [] };
    }
    const g = groups[basePath];
    if (!g.indices.includes(idx)) g.indices.push(idx);
    if (fieldPath && !g.objectFields.includes(fieldPath)) g.objectFields.push(fieldPath);
  });
  Object.values(groups).forEach((g) => {
    g.indices.sort((a, b) => a - b);
    g.objectFields.sort();
  });
  return Object.keys(groups).length > 0 ? groups : undefined;
};
/**
 * Saved validation data can arrive wrapped at an unknown depth with unknown
 * wrapper key names — raw catalog item, `.validationConfig`,
 * `.validationConfig.validations`, `.validations.payload`, already-unwrapped,
 * etc. Rather than hardcoding wrapper names (fragile — breaks the moment an
 * extra layer like "validationConfig" shows up), BFS the object tree and
 * return the first descendant whose OWN keys include one of the schema's
 * top-level property names.
 */
const findValidationRoot = (
  raw: unknown,
  expectedTopKeys: Set<string>,
  maxDepth = 8
): Record<string, unknown> => {
  if (raw == null || typeof raw !== "object") return {};

  const queue: { node: Record<string, unknown>; depth: number }[] = [
    { node: raw as Record<string, unknown>, depth: 0 },
  ];
  const seen = new Set<unknown>();

  while (queue.length > 0) {
    const { node, depth } = queue.shift()!;
    if (seen.has(node)) continue;
    seen.add(node);

    const keys = Object.keys(node);
    if (keys.some((k) => expectedTopKeys.has(k))) {
      return node; // found it — this is the actual validation root
    }

    if (depth >= maxDepth) continue;
    keys.forEach((key) => {
      const child = node[key];
      if (child && typeof child === "object" && !Array.isArray(child)) {
        queue.push({ node: child as Record<string, unknown>, depth: depth + 1 });
      }
    });
  }

  return (raw as Record<string, unknown>) ?? {};
};
export const reconstructParamStates = (validationRule: ValidationRuleEntry[]): Record<string, TmState> => {
  const states: Record<string, TmState> = {};
  validationRule.forEach((tm) => {
    const schemaParams = flattenPayload(
      (tm.value?.payload ?? {}) as Record<string, unknown>,
      "",
      tm.value?.payloadSchema
    );

    const topLevelKeys = new Set(schemaParams.map((p) => p.key.split(/[.[]/)[0]));
    const rawValidation = findValidationRoot(tm.validation, topLevelKeys);

    const savedKeys = flattenValidationKeys(rawValidation);
    const allKeys = Array.from(new Set([...schemaParams.map((p) => p.key), ...savedKeys]));

    const checkedParams: string[] = [];
    const validations: Record<string, ValidationRule> = {};
    allKeys.forEach((key) => {
      const v = getNestedValue(rawValidation, key) as Record<string, unknown> | undefined;
      if (v?.isValidate) {
        checkedParams.push(key);
        if (v.type === "range") {
          validations[key] = { type: "range", min: String(v.min ?? "0"), max: String(v.max ?? "0"), value: "0" };
        } else if (v.type === "enumMatch") {
          validations[key] = { type: "enumMatch", min: String(v.min ?? "0"), max: String(v.max ?? "0"), value: "0" };
        } else {
          validations[key] = { type: v.type === "equalto" ? "equal" : "constant", min: "0", max: "0", value: String(v.value ?? "0") };
        }
      }
    });

    const arrayGroups = buildArrayGroupsFromParamKeys(allKeys);
    states[tm.label] = { checkedParams, validations, ...(arrayGroups ? { arrayGroups } : {}) };
  });
  return states;
};

// ─── PHASE 1: Preview JSON Sync ────────────────────────────────
export interface TmBackendPayload {
  commandId: number;
  saId: number;
  daId: number;
  subsystem: string;
  commandName: string;
  payload: Record<string, unknown>;
}

export const buildBackendTmPayload = (
  paramStates: Record<string, TmState>,
  tmLabel: string,
  tmEntry: any,
  enumMappings?: Record<string, Record<string, string>>,
  paramTypes?: Record<string, string>
): TmBackendPayload | null => {
  if (!tmEntry) return null;
  const tmState = paramStates[tmLabel];

  const params = flattenPayload((tmEntry.payload ?? {}) as Record<string, unknown>, "", tmEntry?.payloadSchema);
  const payload = buildNestedValidation(params, tmState, enumMappings, paramTypes);

  return {
    commandId: tmEntry.commandId,
    saId: tmEntry.saId,
    daId: tmEntry.daId,
    subsystem: tmEntry.subsystem,
    commandName: tmEntry.commandName,
    payload,
  };
};

// ─── PHASE 2 & 3: Validation Config Export/Import ────────────────────────────────
export interface ValidationTemplate {
  id: string;
  name: string;
  commandId: number;
  saId: number;
  daId: number;
  commandName: string;
  subsystem: string;
  createdAt: number;
  tmState: TmState;
}

export interface CopiedValidationConfig {
  type: "validation_config";
  version: "1.0";
  tmLabel: string;
  commandId: number;
  saId: number;
  daId: number;
  commandName: string;
  subsystem: string;
  tmState: TmState;
}

export interface CopiedValidationBundleConfig {
  type: "validation_config_bundle";
  version: "1.0";
  createdAt: number;
  configs: CopiedValidationConfig[];
}

export type ValidationClipboardConfig = CopiedValidationConfig | CopiedValidationBundleConfig;

export const exportValidationRule = (tmLabel: string, tmEntry: any, tmState: TmState): CopiedValidationConfig => ({
  type: "validation_config",
  version: "1.0",
  tmLabel,
  commandId: tmEntry.commandId,
  saId: tmEntry.saId,
  daId: tmEntry.daId,
  commandName: tmEntry.commandName,
  subsystem: tmEntry.subsystem,
  tmState,
});

export const isValidationConfigMatch = (
  config: CopiedValidationConfig,
  targetTmEntry: any,
  allowPartialMatch = false
): boolean => {
  if (!targetTmEntry) return false;
  const matches = config.commandId === targetTmEntry.commandId &&
    config.saId === targetTmEntry.saId &&
    config.daId === targetTmEntry.daId;

  if (matches) return true;

  if (allowPartialMatch) {
    return config.commandId === targetTmEntry.commandId;
  }
  return false;
};

export const importValidationRule = (
  config: CopiedValidationConfig,
  targetTmEntry: any,
  currentParamState: TmState | undefined,
  mergeMode: "replace" | "merge" = "replace"
): { success: boolean; state: TmState; warning?: string } => {
  if (!isValidationConfigMatch(config, targetTmEntry)) {
    return {
      success: false,
      state: currentParamState || { checkedParams: [], validations: {} },
      warning: `TM mismatch: Expected ${config.commandName} (${config.saId}/${config.daId}) but got ${targetTmEntry.commandName} (${targetTmEntry.saId}/${targetTmEntry.daId})`,
    };
  }

  const newState: TmState = mergeMode === "replace"
    ? JSON.parse(JSON.stringify(config.tmState))
    : {
        checkedParams: Array.from(new Set([
          ...(currentParamState?.checkedParams ?? []),
          ...config.tmState.checkedParams,
        ])),
        validations: {
          ...(currentParamState?.validations ?? {}),
          ...config.tmState.validations,
        },
        arrayGroups: {
          ...(currentParamState?.arrayGroups ?? {}),
          ...config.tmState.arrayGroups,
        },
      };

  return { success: true, state: newState };
};

// ─── Template File Export/Import ────────────────────────────────
export const exportTemplateAsFile = (
  tmLabel: string,
  tmEntry: any,
  tmState: TmState
): { success: boolean; error?: string } => {
  try {
    const config: CopiedValidationConfig = exportValidationRule(tmLabel, tmEntry, tmState);
    const jsonStr = JSON.stringify(config, null, 2);
    const blob = new Blob([jsonStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `validation-template-${tmLabel.replace(/\s+/g, "-")}-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return { success: true };
  } catch (err) {
    return { success: false, error: "Failed to save template" };
  }
};

const isCopiedValidationConfig = (config: unknown): config is CopiedValidationConfig => {
  const candidate = config as CopiedValidationConfig;
  return candidate?.type === "validation_config" &&
    candidate.version === "1.0" &&
    Boolean(candidate.tmState) &&
    typeof candidate.tmLabel === "string";
};

const isCopiedValidationBundleConfig = (config: unknown): config is CopiedValidationBundleConfig => {
  const candidate = config as CopiedValidationBundleConfig;
  return candidate?.type === "validation_config_bundle" &&
    candidate.version === "1.0" &&
    Array.isArray(candidate.configs) &&
    candidate.configs.length > 0 &&
    candidate.configs.every(isCopiedValidationConfig);
};

export const parseTemplateFile = (jsonStr: string): { success: boolean; config?: ValidationClipboardConfig; error?: string } => {
  try {
    const config = JSON.parse(jsonStr) as ValidationClipboardConfig;
    if (!isCopiedValidationConfig(config) && !isCopiedValidationBundleConfig(config)) {
      return { success: false, error: "Invalid template format" };
    }
    return { success: true, config };
  } catch {
    return { success: false, error: "Invalid JSON format" };
  }
};

// ─── PHASE 4: Import/Export Files ────────────────────────────────
export interface ExportedTestCaseValidations {
  type: "testcase_validations_export";
  version: "1.0";
  exportDate: string;
  steps: Array<{
    stepIndex: number;
    telecommand: { label: string; value: Record<string, unknown> };
    validations: Array<{
      tmLabel: string;
      tmData: {
        commandId: number;
        saId: number;
        daId: number;
        commandName: string;
        subsystem: string;
      };
      tmState: TmState;
    }>;
  }>;
}

export const exportTestCaseValidations = (
  testCaseSteps: any[],
  paramStates: Record<string, TmState>
): ExportedTestCaseValidations => {
  return {
    type: "testcase_validations_export",
    version: "1.0",
    exportDate: new Date().toISOString(),
    steps: testCaseSteps.map((step, idx) => ({
      stepIndex: idx,
      isCritical: step.isCritical ?? false,
      telecommand: {
        label: step.teleData?.label ?? "",
        value: step.teleData?.value ?? {},
      },
      validations: (step.validationRule ?? []).map((rule: ValidationRuleEntry) => ({
        tmLabel: rule.label,
        tmData: {
          commandId: rule.value.commandId,
          saId: rule.value.saId,
          daId: rule.value.daId,
          commandName: rule.value.commandName,
          subsystem: rule.value.subsystem,
        },
        tmState: paramStates[rule.label] || { checkedParams: [], validations: {} },
      })),
    })),
  };
};

export const parseExportedValidations = (
  jsonStr: string
): { success: boolean; data?: ExportedTestCaseValidations; error?: string } => {
  try {
    const data = JSON.parse(jsonStr) as any;
    if (data.type !== "testcase_validations_export") {
      return { success: false, error: "Invalid file format" };
    }
    if (data.version !== "1.0") {
      return { success: false, error: `Unsupported version: ${data.version}` };
    }
    return { success: true, data };
  } catch (err) {
    return { success: false, error: "Invalid JSON format" };
  }
};

export const importValidationsFromFile = (
  data: ExportedTestCaseValidations,
  currentStepIndex: number,
  currentParamStates: Record<string, TmState>
): { success: boolean; states?: Record<string, TmState>; error?: string } => {
  try {
    if (currentStepIndex >= data.steps.length) {
      return { success: false, error: "Step index out of range" };
    }

    const step = data.steps[currentStepIndex];
    const newParamStates: Record<string, TmState> = { ...currentParamStates };

    step.validations.forEach((val) => {
      newParamStates[val.tmLabel] = JSON.parse(JSON.stringify(val.tmState));
    });

    return { success: true, states: newParamStates };
  } catch (err) {
    return { success: false, error: "Failed to import validations" };
  }
};

/**
 * Derives whether a test case is "critical" from either a top-level flag
 * or by checking if any of its step entries are marked critical.
 *
 * Used in both the TestCases list view and the console TestDropdown to
 * avoid duplicating the same conditional expression.
 *
 * @param tc - A raw test-case object from the API (shape is loosely typed
 *             because several backend response formats are supported).
 * @returns `true` if the test case should be considered critical.
 */
export const deriveIsCritical = (tc: any): boolean =>
  !!tc?.isCritical ||
  (Array.isArray(tc?.testCaseDetails) &&
    tc.testCaseDetails.some((d: any) => d.isCritical));
